import { values } from 'lodash';
import { loadCheckoutScript } from '../../utils/checkoutLoader';
import { loadSubscriptionCheckoutScript } from '../../utils/subscriptionCheckoutLoader';
import { DRAWERS } from '../../utils/constants';

const drawers = [];
for (var i = 0; i < values(DRAWERS).length; i++) {
  drawers.push({ id: values(DRAWERS)[i], zIndex: i, open: false });
}

const state = {
  items: drawers,
  lastOpenedId: null,
  lastOpenedAt: null,
  data: {}
};

const getters = {
  topDrawer(state) {
    return state.items.filter((x) => x.open).sort(({ zIndex: a }, { zIndex: b }) => b - a)[0] || {};
  },

  enableBack(state) {
    return state.items.filter((x) => x.open).length > 1;
  },

  lastOpenedAt(state) {
    return state.lastOpenedAt;
  },

  data: (state) => (drawerId) => {
    return state.data[drawerId] || {};
  },

  items(state) {
    return state.items || [];
  }
};

const actions = {
  async open({ commit, rootGetters, rootState }, req) {
    // Route supported drawers through the existing CheckoutApp embed (print/upload).
    const checkoutRoutes = {
      [DRAWERS.PRINT_CHECKOUT]: { method: 'open', args: () => [req.data || {}] },
      [DRAWERS.UPLOAD_PROJECT]: {
        method: 'openUploadProject',
        args: () => [req.data?.category || '']
      }
    };
    const route = checkoutRoutes[req.drawerId];
    if (route) {
      await loadCheckoutScript({ inDevEnv: rootState.config.inDevEnv });
      const el = document.querySelector('#checkout-component');
      if (el && typeof el[route.method] === 'function') {
        el[route.method](...route.args());
        return;
      }
      throw new Error('Checkout component not ready');
    }

    // Route subscription/credit drawers through the new CheckoutSubscriptionApp embed.
    // Every legacy `commit('drawers/OPEN_DRAWER', DRAWERS.SUBSCRIPTION_CHECKOUT)` call site
    // is routed here transparently — no per-site changes required.
    const subscriptionRoutes = {
      // Upgrade intent: every dashboard entry point into this drawer (Get Unlimited
      // Access CTA, the /subscription redirect, Manage Subscription, plan cards) wants
      // the subscription checkout — NOT the Creative Credits balance screen that the
      // embed's bare `open()` lands on. Route to `openUpgrade` so all of them agree.
      [DRAWERS.SUBSCRIPTION_CHECKOUT]: { method: 'openUpgrade', args: () => [req.data || {}] },
      // Manage intent (the /subscription route): the embed resolves this by subscriber
      // status — active Pros land on the plan-management view, non-subscribers on the
      // upgrade paywall. Kept separate from SUBSCRIPTION_CHECKOUT so the direct
      // upgrade/resubscribe/annual/select-plan flows still force the payment screen.
      [DRAWERS.MANAGE_SUBSCRIPTION_DRAWER]: { method: 'openManage', args: () => [req.data || {}] },
      [DRAWERS.CANCEL_SUBSCRIPTION_DRAWER]: {
        method: 'openCancel',
        args: () => [req.data || {}]
      },
      [DRAWERS.REACTIVATE_SUBSCRIPTION_DRAWER]: {
        method: 'openReactivate',
        args: () => [req.data || {}]
      },
      [DRAWERS.RETRY_PAYMENT_DRAWER]: {
        method: 'openRetryPayment',
        args: () => [req.data || {}]
      }
    };
    const subRoute = subscriptionRoutes[req.drawerId];
    if (subRoute) {
      await loadSubscriptionCheckoutScript({ inDevEnv: rootState.config.inDevEnv });
      const el = document.querySelector('#checkout-subscription-component');
      if (el && typeof el[subRoute.method] === 'function') {
        el[subRoute.method](...subRoute.args());
        return;
      }
      throw new Error('Subscription checkout component not ready');
    }

    // Default: open DashboardApp drawer
    commit('SET_DATA', {
      drawerId: req.drawerId,
      data: req.data || {}
    });
    commit('OPEN_DRAWER', req.drawerId);
  }
};

const mutations = {
  OPEN_DRAWER(state, drawerId) {
    var openDrawers = state.items.filter((x) => x.open);
    state.lastOpenedAt = Date.now();
    if (openDrawers.find((x) => x.id === drawerId)) return;

    var curMaxIndex = Math.max(...openDrawers.map((x) => x.zIndex));
    curMaxIndex = Math.max(curMaxIndex, 100);
    var index = state.items.findIndex((x) => x.id === drawerId);
    state.items[index].open = true;
    state.items[index].zIndex = curMaxIndex + 1;
    state.lastOpenedId = state.items[index].id;
  },

  SET_DATA(state, payload) {
    state.data[payload.drawerId] = payload.data;
  },

  CLEAR_DATA(state, drawerId) {
    if (state.data[drawerId]) state.data[drawerId] = null;
  },

  CLOSE_DRAWER(state, drawerId) {
    var index = state.items.findIndex((x) => x.id === drawerId);
    if (state.data[drawerId]) state.data[drawerId] = null;
    state.items[index].open = false;
  },

  CLOSE_ALL(state) {
    state.items.forEach((x) => {
      x.open = false;
    });
  },

  GO_BACK(state) {
    var topDrawer = state.items
      .filter((x) => x.open)
      .sort(({ zIndex: a }, { zIndex: b }) => b - a)[0];

    var index = state.items.findIndex((x) => x.id === topDrawer.id);
    state.items[index].open = false;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
